import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { appTheme, pitchTheme } from '../../styles/theme';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonStyleProps {
  $variant?: ButtonVariant;
  $size?: ButtonSize;
}

const variantStyles = ($variant: ButtonVariant = 'secondary') => {
  switch ($variant) {
    case 'primary':
      return css`
        border: 1px solid ${appTheme.green};
        background: ${appTheme.green};
        color: #fff;

        &:hover:not(:disabled) {
          border-color: ${appTheme.greenHover};
          background: ${appTheme.greenHover};
        }
      `;
    case 'accent':
      return css`
        border: 1px solid ${appTheme.orange};
        background: ${appTheme.orange};
        color: #fff;

        &:hover:not(:disabled) {
          border-color: ${appTheme.orangeHover};
          background: ${appTheme.orangeHover};
        }
      `;
    case 'ghost':
      return css`
        border: 1px solid transparent;
        background: transparent;
        color: ${appTheme.text};

        &:hover:not(:disabled) {
          background: ${appTheme.surfaceHover};
        }
      `;
    case 'danger':
      return css`
        border: 1px solid ${appTheme.dangerBorder};
        background: ${appTheme.surface};
        color: ${appTheme.dangerText};

        &:hover:not(:disabled) {
          background: ${appTheme.dangerBg};
        }
      `;
    default:
      return css`
        border: 1px solid ${appTheme.border};
        background: ${appTheme.surface};
        color: ${appTheme.text};

        &:hover:not(:disabled) {
          border-color: ${appTheme.green};
          background: ${appTheme.surfaceHover};
        }
      `;
  }
};

/**
 * Shared button styling for both <button> and router <Link> elements.
 */
export const buttonStyles = css<ButtonStyleProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${({ $size }) => {
    if ($size === 'lg') return '14px 28px';
    if ($size === 'sm') return '8px 16px';
    return '10px 20px';
  }};
  border-radius: ${appTheme.radiusPill};
  font-family: ${pitchTheme.fontUi};
  font-size: ${({ $size }) => ($size === 'lg' ? '0.9375rem' : '0.8125rem')};
  font-weight: 600;
  line-height: 1.2;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;

  ${({ $variant }) => variantStyles($variant)}

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Button = styled.button<ButtonStyleProps>`
  ${buttonStyles}
`;

export const ButtonLink = styled(Link)<ButtonStyleProps>`
  ${buttonStyles}
`;
